import { useState, useEffect, useRef } from 'react';
import { Leaf, Star, ArrowRight, Send, Images, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const ProductCard = ({ product, onInquiry, onViewDetail, variant = 'default' }) => {
  const isHighlight = variant === 'highlight';
  const allImages = product.images?.length > 0
    ? product.images
    : product.image_url ? [product.image_url] : [];
  const hasMultiple = allImages.length > 1;

  const [activeIdx, setActiveIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!hasMultiple) return;
    timerRef.current = setInterval(() => {
      setActiveIdx(prev => (prev + 1) % allImages.length);
    }, 3000);
    return () => clearInterval(timerRef.current);
  }, [hasMultiple, allImages.length]);

  return (
    <div
      className={`group relative bg-white overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer ${
        isHighlight
          ? 'rounded-2xl shadow-[0_20px_40px_-12px_rgba(0,0,0,0.1)]'
          : 'rounded-xl shadow-sm hover:shadow-xl'
      }`}
      data-testid={`product-card-${product.id}`}
      onClick={() => onViewDetail?.(product)}
    >
      {/* Image */}
      <div className={`relative overflow-hidden bg-gray-50 ${isHighlight ? 'aspect-[4/3]' : 'aspect-square'}`}>
        {allImages.length > 0 ? (
          <div className="relative w-full h-full">
            {allImages.map((url, idx) => (
              <img
                key={idx}
                src={url}
                alt={`${product.name} ${idx + 1}`}
                width="300"
                height="300"
                loading="lazy"
                decoding="async"
                className={`absolute inset-0 w-full h-full object-contain p-3 transition-all duration-500 ease-in-out transform group-hover:scale-105 ${
                  idx === activeIdx ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ))}
          </div>
        ) : (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 text-sm">No image</div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-[1]">
          {product.match_badge && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-amber-600 to-amber-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-full shadow-md backdrop-blur-sm">
              <Sparkles className="w-3 h-3 animate-pulse" /> {product.match_badge}
            </span>
          )}
          {product.is_eco_friendly && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-600 text-white text-[10px] font-semibold uppercase tracking-wider rounded-full shadow-md" data-testid={`eco-badge-${product.id}`}>
              <Leaf className="w-3 h-3" /> Eco
            </span>
          )}
          {product.is_featured && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#D4AF37] text-white text-[10px] font-semibold uppercase tracking-wider rounded-full shadow-md" data-testid={`featured-badge-${product.id}`}>
              <Star className="w-3 h-3" /> Best Seller
            </span>
          )}
        </div>

        {/* Image dots indicator */}
        {hasMultiple && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-[1]" data-testid={`image-dots-${product.id}`}>
            {allImages.map((_, idx) => (
              <span key={idx}
                className={`block rounded-full transition-all duration-300 ${
                  idx === activeIdx ? 'w-4 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/50'
                }`}
              />
            ))}
          </div>
        )}

        {/* Image count badge */}
        {hasMultiple && (
          <div className="absolute top-3 right-3 z-[1]">
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-black/50 backdrop-blur-sm text-white text-[10px] font-medium rounded-full" data-testid={`image-count-${product.id}`}>
              <Images className="w-3 h-3" /> {allImages.length}
            </span>
          </div>
        )}

        {/* Quick Inquiry Overlay */}
        <div className="absolute inset-x-0 bottom-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out z-[2]">
          <Button
            onClick={(e) => { e.stopPropagation(); onInquiry(product); }}
            className="w-full bg-white/95 backdrop-blur-sm text-[#14532D] hover:bg-white font-semibold rounded-lg shadow-lg h-10 text-sm"
            data-testid={`quick-inquiry-btn-${product.id}`}
          >
            <Send className="w-3.5 h-3.5 mr-2" /> Quick Inquiry
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className={`${isHighlight ? 'p-6' : 'p-4'}`}>
        {product.category_name && (
          <p className="text-[10px] font-semibold uppercase tracking-widest text-[#D4AF37] mb-1.5" data-testid={`category-label-${product.id}`}>
            {product.category_name}
          </p>
        )}
        <h3 className={`font-bold text-gray-900 leading-tight mb-1.5 group-hover:text-[#14532D] transition-colors ${isHighlight ? 'text-xl' : 'text-base'}`}
          style={{ fontFamily: 'Manrope' }}>
          {product.name}
        </h3>
        {product.match_reasons?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {product.match_reasons.map((reason, idx) => (
              <span key={idx} className="text-[10px] px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded font-medium">
                {reason}
              </span>
            ))}
          </div>
        )}
        <p className={`text-gray-500 leading-relaxed line-clamp-2 ${isHighlight ? 'text-sm mb-4' : 'text-xs mb-3'}`}>
          {product.description}
        </p>
        {product.features?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {product.features.slice(0, isHighlight ? 4 : 3).map((f, idx) => (
              <span key={idx} className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded-md font-medium">{f}</span>
            ))}
          </div>
        )}
        <Button
          onClick={(e) => { e.stopPropagation(); onInquiry(product); }}
          variant="outline"
          className="w-full border-[#14532D] text-[#14532D] hover:bg-[#14532D] hover:text-white rounded-sm font-semibold transition-all duration-300 h-9 text-xs"
          data-testid={`inquiry-btn-${product.id}`}
        >
          Send Inquiry
          <ArrowRight className="w-3.5 h-3.5 ml-1.5 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </div>
  );
};

export default ProductCard;
